import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray} from 'ramda-adjunct';

import {useDeleteCarPassConfigMutation} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {catchUnhandledDataGridActions, DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const PREDEFINED_COLUMNS = [] as const;
const title = i18n.t('page.integrations.labels.carpassConfig');

export function CarpassSettings() {
  const navigate = useNavigate();
  const [deleteCarPassConfig] = useDeleteCarPassConfigMutation();

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    refreshData,
  }) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('edit', () => {
        navigate(composePath(settingsRoutes.vehicleServicesCarpassDetail, {params: {id}}));
      })
      .with('delete', () => {
        openDeleteDialog({
          onConfirm: () =>
            deleteCarPassConfig({branchId: id})
              .unwrap()
              .then(() => refreshData())
              .then(() =>
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'))
              )
              .catch(handleApiError),
          text: i18n.t('page.integrations.labels.carpassConfig.deleteConfirm'),
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate
        header={{
          title,
          breadcrumbs: [
            {
              label: i18n.t('page.integrations.labels.vehicleServices'),
              href: settingsRoutes.vehicleServices,
            },
          ],
          actions: [
            {
              type: 'button',
              title: i18n.t('page.integrations.labels.carpassConfig.new'),
              onClick: () =>
                navigate(
                  composePath(settingsRoutes.vehicleServicesCarpassDetail, {params: {id: 'new'}})
                ),
            },
          ],
        }}
        data-testid={testIds.settings.vehicleServicesCarpass('settingsTemplate')}
      >
        <DmsDataGrid
          gridCode="car-pass-config"
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          data-testid={testIds.settings.vehicleServicesCarpass('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
}
